import React, { useState, useEffect } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import CONFIG from "./config";
import { useTranslation } from 'react-i18next';

const EventsLookup = () => {
    const { t } = useTranslation(['common', 'events']);
    const [bookingRef, setBookingRef] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [event, setEvent] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [autoSearched, setAutoSearched] = useState(false);

    // Auto-search when query parameters are present
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const bookingRefParam = queryParams.get('booking_ref');
        const emailParam = queryParams.get('email');
        
        if (bookingRefParam && emailParam && !autoSearched) {
            setBookingRef(bookingRefParam);
            setCustomerEmail(emailParam);
            setAutoSearched(true);
            // Trigger search automatically
            performSearch(bookingRefParam, emailParam);
            
            // Remove query parameters from URL without reloading
            const newUrl = window.location.pathname;
            window.history.replaceState(null, "", newUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const performSearch = async (ref, email) => {
        setError("");
        setEvent(null);
        setLoading(true);

        try {
            const response = await axios.get(`${CONFIG.BACKEND_URL}/api/events/lookup`, {
                params: {
                    booking_ref: ref.trim(),
                    customer_email: email.trim()
                }
            });

            setEvent(response.data);
        } catch (err) {
            setError(err.response?.data?.error || t('events:failedToFindEvent'));
            setEvent(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        performSearch(bookingRef, customerEmail);
    };

    const handleCancelEvent = async () => {
        setCancelling(true);
        try {
            await axios.post(`${CONFIG.BACKEND_URL}/api/events/cancel`, {
                booking_ref: bookingRef.trim(),
                customer_email: customerEmail.trim()
            });
            
            setEvent(null);
            setBookingRef("");
            setCustomerEmail("");
            setShowCancelConfirm(false);
            setError("");
            alert(t('events:eventCancelledSuccess'));
        } catch (err) {
            setError(err.response?.data?.error || t('events:failedToCancelEvent'));
        } finally {
            setCancelling(false);
            setShowCancelConfirm(false);
        }
    };

    const formatEventTime = (startTime, endTime) => {
        try {
            const start = DateTime.fromISO(startTime);
            const end = DateTime.fromISO(endTime);
            return `${start.toLocaleString({ weekday: 'short', month: 'long', day: 'numeric' })} • ${start.toLocaleString(DateTime.TIME_SIMPLE)} – ${end.toLocaleString(DateTime.TIME_SIMPLE)}`;
        } catch (err) {
            return `${startTime} - ${endTime}`;
        }
    };

    const businessUrl = event && event.business_id 
        ? `${CONFIG.FRONTEND_URL}/${event.business_id}`
        : "";

    return (
        <div>
            <div style={styles.container}>
                <h1 style={styles.title}>{t('events:eventLookup')}</h1>

                <form onSubmit={handleSearch}>
                    <div style={styles.formGroup}>
                        <label htmlFor="bookingRef" style={styles.label}>
                            {t('events:bookingReferenceNumber')}
                        </label>
                        <input
                            id="bookingRef"
                            type="text"
                            value={bookingRef}
                            onChange={(e) => setBookingRef(e.target.value)}
                            style={styles.input}
                            placeholder={t('events:enterBookingReference')}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="customerEmail" style={styles.label}>
                            {t('events:customerEmailAddress')}
                        </label>
                        <input
                            id="customerEmail"
                            type="email"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            style={styles.input}
                            placeholder={t('events:enterEmailAddress')}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            ...styles.button,
                            opacity: loading ? 0.5 : 1,
                            cursor: loading ? "not-allowed" : "pointer"
                        }}
                    >
                        {loading ? t('events:searching') : t('events:search')}
                    </button>
                </form>

                {error && (
                    <div style={styles.error}>
                        {error}
                    </div>
                )}

                {event && (
                    <div style={styles.eventCard}>
                        <h2 style={styles.sectionTitle}>{t('events:eventDetails')}</h2>
                        
                        <div style={styles.eventDetails}>
                            <div style={styles.detailRow}>
                                <span style={styles.detailLabel}>{t('events:businessName')}</span>
                                <span style={styles.detailValue}>{event.business_name || t('events:notAvailable')}</span>
                            </div>

                            <div style={styles.detailRow}>
                                <span style={styles.detailLabel}>{t('events:serviceName')}</span>
                                <span style={styles.detailValue}>{event.services || t('events:notAvailable')}</span>
                            </div>

                            <div style={styles.detailRow}>
                                <span style={styles.detailLabel}>{t('events:eventTime')}</span>
                                <span style={styles.detailValue}>{formatEventTime(event.start_time, event.end_time)}</span>
                            </div>

                            {businessUrl && (
                                <div style={styles.detailRow}>
                                    <span style={styles.detailLabel}>{t('events:businessUrl')}</span>
                                    <span style={styles.detailValue}>
                                        <a 
                                            href={businessUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={styles.link}
                                        >
                                            {businessUrl}
                                        </a>
                                    </span>
                                </div>
                            )}
                        </div>

                        <div style={styles.cancelButtonContainer}>
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                style={{
                                    ...styles.button,
                                    backgroundColor: "#dc3545",
                                    width: "100%"
                                }}
                            >
                                {t('events:cancelEvent')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Cancel Confirmation Modal */}
                {showCancelConfirm && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modalContent}>
                            <h3 style={styles.modalTitle}>
                                {t('events:confirmCancellation')}
                            </h3>
                            <p style={styles.modalText}>
                                {t('events:cancelConfirmationMessage')}
                            </p>
                            <div style={styles.modalButtons}>
                                <button
                                    onClick={() => setShowCancelConfirm(false)}
                                    style={{
                                        ...styles.button,
                                        backgroundColor: "#6c757d",
                                        flex: 1,
                                        marginRight: "10px"
                                    }}
                                    disabled={cancelling}
                                >
                                    {t('events:no')}
                                </button>
                                <button
                                    onClick={handleCancelEvent}
                                    style={{
                                        ...styles.button,
                                        backgroundColor: "#dc3545",
                                        flex: 1,
                                        opacity: cancelling ? 0.5 : 1,
                                        cursor: cancelling ? "not-allowed" : "pointer"
                                    }}
                                    disabled={cancelling}
                                >
                                    {cancelling ? t('events:cancelling') : t('events:yes')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div style={styles.footer}>
                <p>{t('common:poweredBy')} 
                {" "}
                <a href={`${CONFIG.FRONTEND_URL}/`} target="_blank" rel="noopener noreferrer">{t('common:appName')}</a>
                {" "}|{" "}
                <a href={`${CONFIG.FRONTEND_URL}/privacy`} target="_blank" rel="noopener noreferrer">{t('common:privacyPolicy')}</a>
                {" "}|{" "}
                <a href={`${CONFIG.FRONTEND_URL}/terms`} target="_blank" rel="noopener noreferrer">{t('common:termsOfService')}</a>
                </p>
            </div>
        </div>
    );
};

const styles = {
    container: { 
        padding: "20px", 
        maxWidth: "800px", 
        margin: "0 auto" 
    },
    title: { 
        fontSize: "32px", 
        marginBottom: "20px",
        color: "#2c3e50"
    },
    formGroup: {
        marginBottom: "20px"
    },
    label: { 
        display: "block", 
        marginBottom: "5px", 
        fontWeight: "bold",
        color: "#2c3e50"
    },
    input: { 
        display: "block", 
        width: "100%", 
        padding: "10px", 
        marginBottom: "10px", 
        borderRadius: "5px", 
        border: "1px solid #ccc",
        fontSize: "16px",
        boxSizing: "border-box"
    },
    button: { 
        padding: "10px 20px", 
        backgroundColor: "#2c3e50", 
        color: "#fff", 
        border: "none", 
        borderRadius: "5px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        fontSize: "16px",
        fontWeight: "bold"
    },
    error: { 
        color: "red", 
        marginTop: "10px",
        padding: "10px",
        backgroundColor: "#ffebee",
        borderRadius: "5px",
        border: "1px solid #ffcdd2"
    },
    eventCard: {
        marginTop: "30px",
        padding: "20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)"
    },
    sectionTitle: {
        fontSize: '24px',
        marginBottom: '20px',
        color: '#2c3e50',
    },
    eventDetails: {
        marginBottom: "20px"
    },
    detailRow: {
        marginBottom: "15px",
        display: "flex",
        flexDirection: "column"
    },
    detailLabel: {
        fontWeight: "bold",
        color: "#555",
        marginBottom: "5px"
    },
    detailValue: {
        color: "#2c3e50"
    },
    link: {
        color: "#2c3e50",
        textDecoration: "underline"
    },
    cancelButtonContainer: {
        marginTop: "20px",
        paddingTop: "20px",
        borderTop: "1px solid #ddd"
    },
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
    },
    modalContent: {
        backgroundColor: "#fff",
        borderRadius: "8px",
        padding: "30px",
        maxWidth: "500px",
        width: "90%",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
    },
    modalTitle: {
        fontSize: "20px",
        fontWeight: "bold",
        marginBottom: "15px",
        color: "#2c3e50"
    },
    modalText: {
        marginBottom: "25px",
        color: "#555",
        lineHeight: "1.6"
    },
    modalButtons: {
        display: "flex",
        justifyContent: "space-between"
    },
    footer: {
        marginTop: "50px",
        padding: "0px",
        backgroundColor: "rgb(226, 218, 195)",
        textAlign: "center",
        width: "100%",  
        display: "flex",
        justifyContent: "center",
        fontSize: "12px",
        height: "40px",
        alignItems: "center"
    }
};

export default EventsLookup;

